"use client";

import { useQuery } from "@tanstack/react-query";

// Types are intentionally loose here — the component defines rich local types
// and uses them for type narrowing. The hook just handles fetch + cache.

async function fetchCryptoSidebar(): Promise<any> {
  const res = await fetch("/api/markets/crypto/sidebar");
  const data = await res.json();
  return data.success && data.data ? data.data : null;
}

async function fetchFinanceSidebar(): Promise<any> {
  const res = await fetch("/api/markets/finance/sidebar");
  const data = await res.json();
  return data.success && data.data ? data.data : null;
}

export function useCryptoSidebar(enabled: boolean) {
  return useQuery<any>({
    queryKey: ["markets", "crypto", "sidebar"],
    queryFn: fetchCryptoSidebar,
    staleTime: 8 * 60 * 60 * 1000, // 8 hours (scraper runs every 8h)
    gcTime: 24 * 60 * 60 * 1000,
    enabled,
  });
}

export function useFinanceSidebar(enabled: boolean) {
  return useQuery<any>({
    queryKey: ["markets", "finance", "sidebar"],
    queryFn: fetchFinanceSidebar,
    staleTime: 8 * 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    enabled,
  });
}

// Kalshi sidebar — works for any category (crypto, sports, politics, etc.)
// Returns: { category, total, frequencies: [{label, value, count}], subcategories: [{label, count}] }
export interface KalshiSidebarData {
  category: string;
  total: number;
  frequencies: Array<{ label: string; value: string; count: number }>;
  // `children` present when Kalshi exposes a scope-level nesting under
  // the tag (e.g. Oil & Gas → WTI Crude / Brent Crude / Natural Gas /
  // Heating Oil). Optional — pre-v9 responses and categories without a
  // scope dimension omit it.
  subcategories: Array<{
    label: string;
    count: number;
    children?: Array<{ label: string; count: number }>;
  }>;
  items?: Array<{ kind: 'frequency' | 'tag'; id: string; label: string; count: number }>;
}

async function fetchKalshiSidebar(category: string): Promise<KalshiSidebarData | null> {
  const res = await fetch(`/api/markets/kalshi/sidebar/${encodeURIComponent(category)}`);
  if (!res.ok) return null;
  return res.json();
}

// Kalshi Sports sidebar — nested tree with sport → competitions + scope counts
export interface KalshiSportsSidebarData {
  total: number;
  totalScopeCounts: Record<string, number>;
  sports: Array<{
    sport: string;
    count: number;
    scopes: string[];
    scopeCounts: Record<string, number>;
    competitions: Array<{
      name: string;
      count: number;
      scopeCounts: Record<string, number>;
    }>;
  }>;
  generatedAt: number;
}

async function fetchKalshiSportsSidebar(): Promise<KalshiSportsSidebarData | null> {
  const res = await fetch('/api/markets/kalshi/sports-sidebar');
  if (!res.ok) return null;
  return res.json();
}

export function useKalshiSportsSidebar(enabled: boolean) {
  return useQuery<KalshiSportsSidebarData | null>({
    queryKey: ['markets', 'kalshi', 'sports-sidebar'],
    queryFn: fetchKalshiSportsSidebar,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    enabled,
  });
}

export function useKalshiSidebar(
  category: string,
  enabled: boolean,
  initialData?: KalshiSidebarData | null
) {
  return useQuery<KalshiSidebarData | null>({
    queryKey: ["markets", "kalshi", "sidebar", category],
    queryFn: () => fetchKalshiSidebar(category),
    staleTime: 15 * 60 * 1000, // 15 min (matches backend cache)
    gcTime: 60 * 60 * 1000,
    enabled: enabled && !!category,
    initialData: initialData ?? undefined,
  });
}
